import React from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import LinearProgress from '@material-ui/core/LinearProgress';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

// ─── Styles ──────────────────────────────────────────────────────────────────
// Mirrors FilteredHorizontalTabPage's own header treatment (templateHeader sitting
// directly on top of a Paper toolbar bar, sharing a border with no gap) — this is the
// "no tabs" sibling of that layout: a page header with a filter/selector toolbar
// folded directly into the same heading region, instead of a separate stacked card.

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.grey[100],
    },

    // ── Header ──
    templateHeader: {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.25, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      flexShrink: 0,
    },
    backButton: {
      color: theme.palette.text.secondary,
      padding: 6,
      // Same small nudge-left on hover as the Enquiry form's header back link (headerBackLink in enquiryFormDrawerStyles.ts).
      '& .MuiSvgIcon-root': {
        transition: 'transform 0.15s ease',
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        '& .MuiSvgIcon-root': {
          transform: 'translateX(-2px)',
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:hover .MuiSvgIcon-root': {
          transform: 'none',
        },
      },
    },
    headerDivider: {
      width: 1,
      height: 28,
      backgroundColor: theme.palette.divider,
      marginRight: theme.spacing(0.25),
      flexShrink: 0,
    },
    templateIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: '#e3f2fd',
      color: theme.palette.primary.main,
      flexShrink: 0,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    templateTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    templateSubtitle: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginTop: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      flexShrink: 0,
    },

    // ── Toolbar row (filters + actions) — attached directly under the header, no
    // gap, so the header + toolbar read as one heading region (same pairing as
    // FilteredHorizontalTabPage's templateHeader + tabBar). ──
    toolbarBar: {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.07)',
      flexShrink: 0,
    },
    toolbarRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      padding: theme.spacing(1.25, 2),
    },
    filtersArea: {
      display: 'flex',
      alignItems: 'flex-end',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      flex: 1,
      minWidth: 0,
    },
    actionsArea: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexShrink: 0,
      marginLeft: 'auto',
    },
    loadingBar: {
      height: 2,
    },

    // ── Content ──
    contentArea: {
      flex: 1,
      overflow: 'auto',
      padding: theme.spacing(2.5),
    },
  })
);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FilteredHeaderPageProps {
  /** Icon rendered in the header's rounded avatar — match the sidebar nav icon for this page */
  icon: React.ReactElement;
  /** Main page title */
  title: string;
  /** Optional subtitle shown below the title */
  subtitle?: string;
  /** Optional controls rendered at the right end of the header row (e.g. help/resource links) */
  headerActions?: React.ReactNode;
  /** If provided, renders a back arrow button that calls this handler */
  onBack?: () => void;
  /** Tooltip label for the back button. @default 'Back' */
  backTooltip?: string;
  /**
   * Filter/selector controls rendered on the left of the toolbar row that sits
   * directly under the header — e.g. scope/role/user selectors.
   */
  filters?: React.ReactNode;
  /** Action buttons (e.g. Save/Reset) rendered at the end of the toolbar row */
  actions?: React.ReactNode;
  /** Shows a thin loading bar under the toolbar row while data is being fetched */
  loading?: boolean;
  /** Extra elements rendered alongside the page but outside the layout flow (e.g. a confirmation dialog) */
  dialogs?: React.ReactNode;
  /** Main scrollable page content */
  children: React.ReactNode;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const FilteredHeaderPage: React.FC<FilteredHeaderPageProps> = ({
  icon,
  title,
  subtitle,
  headerActions,
  onBack,
  backTooltip = 'Back',
  filters,
  actions,
  loading = false,
  dialogs,
  children,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>

      {dialogs}

      {/* ── Header ── */}
      <div className={classes.templateHeader}>
        {onBack && (
          <>
            <Tooltip title={backTooltip} placement="bottom">
              <IconButton
                className={classes.backButton}
                size="small"
                onClick={onBack}
                aria-label={backTooltip}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <div className={classes.headerDivider} />
          </>
        )}
        <Avatar className={classes.templateIcon} variant="rounded">
          {icon}
        </Avatar>
        <div className={classes.headerText}>
          <Typography className={classes.templateTitle}>{title}</Typography>
          {subtitle && (
            <Typography className={classes.templateSubtitle}>{subtitle}</Typography>
          )}
        </div>
        {headerActions && <div className={classes.headerActions}>{headerActions}</div>}
      </div>

      {/* ── Toolbar: filters + actions, folded into the same heading region ── */}
      {(filters || actions) && (
        <Paper className={classes.toolbarBar} square={true} elevation={0}>
          <div className={classes.toolbarRow}>
            {filters && <div className={classes.filtersArea}>{filters}</div>}
            {actions && <div className={classes.actionsArea}>{actions}</div>}
          </div>
          {loading && <LinearProgress className={classes.loadingBar} />}
        </Paper>
      )}

      {/* ── Content ── */}
      <div className={classes.contentArea}>
        {children}
      </div>

    </div>
  );
};
